import { type ChangeEvent, useContext, useState } from "react";
import { useCookies } from "react-cookie";
import { Link, useNavigate } from "react-router-dom";

import { Button } from "@/app/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/app/components/ui/dialog";
import { Input } from "@/app/components/ui/input";
import { Label } from "@/app/components/ui/label";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/app/components/ui/tooltip";
import type { ResumeContextValue, SelectedExtras } from "@/app/types/resume";

import tips from "../../../../assets/copy/tips";
import Tips from "../../../../components/Tips";
import ResumeContext from "../../../../context/ResumeContext";
import { logToConsole } from "../../../../handlers/utilityFunctions";
import JobSeekerRegistrationForm from "../../../Auth/RegisterScreen/JobSeekerRegistrationForm";
import ExtrasCheckbox, { type ExtrasSection } from "./components/ExtrasCheckbox";

const extrasSections: ExtrasSection[] = [
	{ id: "accomplishments", label: "Accomplishments", name: "Accomplishments", link: "/resume/accomplishments" },
	{ id: "affiliations", label: "Affiliations", name: "Affiliations", link: "/resume/affiliations" },
	{ id: "websites", label: "Websites, Portfolios, Profiles", name: "Websites", link: "/resume/personal-links" },
	{
		id: "additionalInformation",
		label: "Additional Information",
		name: "AdditionalInformation",
		link: "/resume/additional-information",
	},
	{ id: "certifications", label: "Certifications", name: "Certifications", link: "/resume/certifications" },
	{ id: "languages", label: "Languages", name: "Languages", link: "/resume/languages" },
	{ id: "reference", label: "Referees", name: "Reference", link: "/resume/reference-summary" },
	{ id: "profile", label: "Personal Profile", name: "Profile", link: "/resume/personal-profile" },
];

/** Mirrors `@MaxLength(15)` on the server's `alias` field. */
const CUSTOM_EXTRA_NAME_MAX = 15;

const sectionKeys: Record<string, keyof Omit<SelectedExtras, "customExtra">> = {
	Accomplishments: "accomplishments",
	Affiliations: "affiliations",
	Websites: "websites",
	AdditionalInformation: "additionalInformation",
	Certifications: "certifications",
	Languages: "languages",
	Profile: "profile",
	Reference: "reference",
};

const Extras = () => {
	const [cookies] = useCookies(["resume"]);
	const navigate = useNavigate();
	const [showLoginPopup, setShowLoginPopup] = useState(
		(cookies as Record<string, unknown>)["Authorization"] ? false : true,
	);
	const { resumeData, setResumeData } = useContext(ResumeContext) as ResumeContextValue;
	const selectedExtras = resumeData.extras.selectedExtras;
	const updateSelectedExtra = <K extends keyof SelectedExtras>(key: K, value: SelectedExtras[K]) => {
		setResumeData((current) => ({
			...current,
			extras: {
				...current.extras,
				selectedExtras: { ...current.extras.selectedExtras, [key]: value },
			},
		}));
	};

	const handleCustomExtra = (event: ChangeEvent<HTMLInputElement>) => {
		updateSelectedExtra("customExtra", event.target.value);
	};

	const extrasChoicesHandler = (event: ChangeEvent<HTMLInputElement>) => {
		const section = sectionKeys[event.target.id];

		if (!section) {
			logToConsole("Not Accomplishments");
			return;
		}

		updateSelectedExtra(section, event.target.checked);
	};

	return (
		<>
			<Dialog open={showLoginPopup} onOpenChange={setShowLoginPopup}>
				<DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-md">
					<DialogHeader>
						<DialogTitle>Register to save progress</DialogTitle>
						<DialogDescription>
							Please sign up to save your resume progress and continue editing on other devices.
						</DialogDescription>
					</DialogHeader>
					<JobSeekerRegistrationForm />
					<DialogFooter>
						<Button type="button" variant="secondary" onClick={() => setShowLoginPopup(false)}>
							Stay
						</Button>
						<Link to="/register">
							<Button type="button">Register</Button>
						</Link>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			<div className="min-h-screen">
				<div className="w-full md:max-w-xl">
					<div className="mb-2 flex justify-end">
						<Tips content={tips.extras} />
					</div>
					<p className="mb-3 text-gray-700 text-sm">Do you have anything else to add?</p>

					{extrasSections.map((section) => {
						return (
							<ExtrasCheckbox
								section={section}
								extrasChoicesHandler={extrasChoicesHandler}
								navigate={navigate}
								key={section.id}
							/>
						);
					})}

					<div className="mb-4">
						<Label htmlFor="CustomExtra" className="mb-1 block text-sm">
							Add Your Own
						</Label>
						<div className="relative flex items-center justify-between">
							<Input
								type="text"
								id="CustomExtra"
								name="CustomExtra"
								value={selectedExtras.customExtra}
								onChange={handleCustomExtra}
								placeholder="Hobbies"
								// The server caps `alias` at 15 characters. Without this the reader
								// could name a section, walk to its detail page, write the whole
								// entry, and only then be told the name was too long — on a screen
								// where the name cannot be edited.
								maxLength={CUSTOM_EXTRA_NAME_MAX}
								aria-describedby="CustomExtra-hint"
							/>
							{selectedExtras.customExtra !== "" && (
								<Tooltip>
									<TooltipTrigger asChild>
										<button
											type="button"
											onClick={() => navigate(`/resume/custom-extra/${selectedExtras.customExtra}`)}
											className="absolute right-3 cursor-pointer font-semibold text-primary-700"
										>
											Update
										</button>
									</TooltipTrigger>
									<TooltipContent side="right" variant="plain">
										Click to Add/Edit
									</TooltipContent>
								</Tooltip>
							)}
						</div>
						<p id="CustomExtra-hint" className="mt-1 text-gray-600 text-xs">
							Up to {CUSTOM_EXTRA_NAME_MAX} characters.
						</p>
					</div>
					<div className="flex items-center justify-end gap-2">
						<button
							type="button"
							name="back"
							onClick={() => {
								navigate("/my-resumes");
							}}
							className="cursor-pointer font-semibold text-primary-700"
						>
							Skip for now
						</button>
						<Button
							type="button"
							onClick={() => {
								navigate("/resume/review");
							}}
						>
							Review
						</Button>
					</div>
				</div>
			</div>
		</>
	);
};

export default Extras;
